/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { useI18n } from "hooks/i18n/i18n";
import { withNoTranslate } from "i18n/noTranslate";

import type { FC } from "react";

type Props = {
  spwnId: string;
  userPhotoURL: string;
  userDisplayName: string;
  userEmail: string;
  onCopySpwnId: () => void;
  isShowCopiedFeedback: boolean;
};

export const AccountRegisterInfo: FC<Props> = ({
  spwnId,
  userPhotoURL,
  userDisplayName,
  userEmail,
  onCopySpwnId,
  isShowCopiedFeedback,
}) => {
  const { t } = useI18n();
  const classes = styles();

  return (
    <div css={classes.root}>
      <div css={classes.layout}>
        <img css={classes.thumbnail} src={userPhotoURL} alt={userDisplayName} />

        <div css={classes.profile}>
          <p css={classes.name} {...withNoTranslate()}>
            {userDisplayName}
          </p>
          <p css={classes.address} {...withNoTranslate()}>
            {userEmail}
          </p>
        </div>

        <div css={classes.spwnId}>
          <p css={classes.spwnIdHeadline}>
            {t("account.spwnId")}
            <button
              css={[
                classes.copyIdButton,
                isShowCopiedFeedback ? classes.copyIdButtonCopied : undefined,
              ]}
              type="button"
              onClick={onCopySpwnId}
            >
              {isShowCopiedFeedback ? (
                <CheckCircleIcon css={classes.copiedIcon} aria-hidden="true" />
              ) : null}
              <span role="status" aria-live="polite">
                {isShowCopiedFeedback
                  ? t("account.copiedId")
                  : t("account.copyId")}
              </span>
            </button>
          </p>
          <p css={classes.spwnIdValue} {...withNoTranslate()}>
            {spwnId}
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = () => ({
  root: css`
    padding: 20px 5%;
    border-bottom: 1px solid #e6e5e6;
    @media screen and (min-width: 768px) {
      padding: 30px 0 40px;
    }
  `,
  layout: css`
    display: grid;
    grid-template-areas:
      "thumbnail profile"
      "spwnId spwnId";
    grid-template-columns: 64px minmax(0, 1fr);
    align-items: center;
    column-gap: 16px;
    row-gap: 20px;
    @media screen and (min-width: 768px) {
      grid-template-areas:
        "thumbnail profile"
        "thumbnail spwnId";
      grid-template-columns: 200px 520px;
      column-gap: 40px;
      row-gap: 0px;
      width: 760px;
      margin: 0 auto;
    }
  `,
  thumbnail: css`
    grid-area: thumbnail;
    width: 64px;
    height: 64px;
    border: 2px solid #dedede;
    border-radius: 50%;
    object-fit: cover;
    @media screen and (min-width: 768px) {
      width: 200px;
      height: 200px;
    }
  `,
  profile: css`
    grid-area: profile;
  `,
  name: css`
    margin-bottom: 5px;
    font-size: 18px;
    font-weight: bold;
    @media screen and (min-width: 768px) {
      margin-bottom: 10px;
      font-size: 30px;
    }
  `,
  address: css`
    color: #8a8f8f;
    font-size: 12px;
    @media screen and (min-width: 768px) {
      font-size: 20px;
    }
  `,
  spwnId: css`
    grid-area: spwnId;
  `,
  spwnIdHeadline: css`
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    font-size: 13px;
    @media screen and (min-width: 768px) {
      font-size: 18px;
    }
  `,
  copyIdButton: css`
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 130px;
    padding: 0;
    margin-left: 15px;
    font-family: inherit;
    color: #19d0d8;
    font-size: 12px;
    font-weight: bold;
    line-height: inherit;
    background: none;
    border: 0;
    cursor: pointer;
  `,
  copyIdButtonCopied: css`
    color: #19796d;
  `,
  copiedIcon: css`
    &.MuiSvgIcon-root {
      display: block;
      font-size: 16px;
    }
  `,
  spwnIdValue: css`
    font-family: "Share Tech Mono", monospace;
    font-size: 20px;
    font-weight: bold;
    letter-spacing: -1px;
    @media screen and (min-width: 768px) {
      font-size: 30px;
    }
  `,
});
